import React from "react";
import { useNavigate } from "react-router-dom";
import { useContext } from "react";
import { PlatformContext } from "../components/Platform/PlatformContext";
import Button from "../components/Form/Button";

const RuleSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="mb-6">
    <h2 className="text-2xl font-semibold mb-4">{title}</h2>
    {children}
  </div>
);

const NestedList: React.FC<{ items: string[] }> = ({ items }) => (
  <ul className="list-disc pl-10">
    {items.map((item, index) => (
      <li key={index}>{item}</li>
    ))}
  </ul>
);

const Rules: React.FC = () => {
  let navigate = useNavigate();
  let platform = useContext(PlatformContext);
  const isMobile = !platform?.isDesktop;

  return (
    <div className="flex flex-col w-screen items-center justify-center">
      <h1 className="text-3xl font-bold text-white mt-10">FFL League Rules</h1>

      <div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-lg p-6 m-6 max-w-3xl text-white shadow-lg border border-white/20">

        {/* Application and Membership */}
        <RuleSection title="Application and Membership">
          <ul className="list-disc pl-5">
            <li>Each team has one captain, responsible for communication between the FFL and their team</li>
            <li>Each team must have at least 20 players to register, with no upper limit</li>
            <li>The season will take place during the summer semester</li>
          </ul>
        </RuleSection>

        {/* Game Guidelines */}
        <RuleSection title="Game Guidelines">
          <ul className="list-disc pl-5">
            <li>
              The League is set in a two-group format:
              <NestedList
                items={[
                  "The two groups will be drawn randomly at the beginning of each season",
                  "Each group will play a single round-robin format",
                  "A win grants 3 points, a draw gives 1 point, and a loss gives none",
                  "Placement is determined by points, goal difference, goals scored, and least goals conceded, respectively",
                  "After the round-robin, the top four teams from each group enter a knockout tournament",
                  "Matchups for the play-off tournament are based on group placement"
                ]}
              />
            </li>
            <li>Matches are played in an 11 vs 11 format for 90 minutes</li>
            <li>Breaks are agreed on before each match, e.g. 2×45 minutes or 3×30 minutes</li>
            <li>Offside rules are enforced</li>
            <li>No substitution limits; teams can substitute players freely</li>
          </ul>
        </RuleSection>

        {/* Score Management */}
        <RuleSection title="Score Management">
          <ul className="list-disc pl-5">
            <li>Match dates and kickoff times must be agreed upon by both team captains</li>
            <li>All matches must be played before the weekly deadline; unplayed matches will be recorded as a draw</li>
            <li>Captains must enter the match score in the league dashboard. Once both captains submit the same score, it will be officially recorded in the league table</li>
          </ul>
        </RuleSection>

        {/* Officiating & Fair Play */}
        <RuleSection title="Officiating & Fair Play">
          <ul className="list-disc pl-5">
            <li>Each match will be officiated by two neutral players from other teams in the league</li>
            <li>Team captains must assign two players from their team to officiate the upcoming week's matches</li>
            <li>Failure to assign referees or fulfill officiating duties will result in penalties</li>
            <li>
              <span className="font-semibold">This rule only applies to matches played in the correct week. For postponed or late matches, refereeing is no longer mandatory.</span>
            </li>
          </ul>
        </RuleSection>

        {/* Rule for Postponing Matches */}
        <RuleSection title="Postponing Matches">
          <ul className="list-disc pl-5">
            <li>
              A team is allowed to reschedule a match from previous weeks. However, if the team is unable to participate in the match of the current week due to the rescheduled game, the match that is planned for that week will automatically be counted as a loss for that team.
            </li>
            <li>
              Rescheduling is not mandatory. If a match is not rescheduled, it will automatically be recorded as a 0:0 draw.
            </li>
            <li>
              This does not touch the rule that if a team cannot play in the week the game is originally scheduled, the game will be recorded as a 0:3 loss for that team. It only affects cases where games are interrupted or both captains agree to postpone the match to a fixed date.
            </li>
            <li>
              In special cases – for example, if a team refuses to play or makes it unnecessarily difficult to find a new date – the organizers reserve the right to decide the outcome of the match in the interest of fairness.
            </li>
          </ul>
        </RuleSection>

        {/* Penalties */}
        <RuleSection title="Penalties">
          <ul className="list-disc pl-5">
            <li>Every team starts the season with a <strong>credit score of 1</strong></li>
            <li>Failing to assign a referee or missing an officiating duty will result in a <strong>1-credit deduction</strong></li>
            <li>For each <strong>negative credit</strong>, the team will <strong>lose 2 points</strong> in the league standings</li>
          </ul>
        </RuleSection>

      </div>
    </div>
  );
};

export default Rules;
